import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * PinLoginForm — the workshop PIN: typed in, or carried by a PIN link
 * (`/?pin=…`, from the admin's PIN menu), which fills it in and signs in at
 * once. The front page's PIN view (AdminLogin) draws it; `onLogin` gets
 * { instanceId, isAdmin, session } as before.
 */

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-8px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-8px); }
  80% { transform: translateX(8px); }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PinInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  background: #0a0a0a;
  border: 1px solid ${props => props.$error ? '#ef4444' : '#222'};
  border-radius: 4px;
  color: #00d4ff;
  font-size: 24px;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  text-align: center;
  letter-spacing: 8px;
  text-transform: uppercase;
  transition: all 0.2s ease;
  box-sizing: border-box;
  animation: ${props => props.$error ? shake : 'none'} 0.4s ease;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);

  &:focus {
    outline: none;
    border-color: ${props => props.$error ? '#ef4444' : '#222'};
    background: #0a0a0a;
  }

  &::placeholder {
    color: #444;
    letter-spacing: 2px;
    text-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  text-align: center;
  padding: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #333;
  border-radius: 4px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: ${props => props.$ready
    ? 'linear-gradient(135deg, #1a237e 0%, #4a148c 100%)'
    : '#1a1a1a'};
  border: 1px solid ${props => props.$ready ? '#3949ab' : '#333'};
  border-radius: 4px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  ${props => props.$ready && `
    box-shadow: 0 0 20px rgba(74, 20, 140, 0.4);
  `}

  &:hover:not(:disabled) {
    background: ${props => props.$ready
      ? 'linear-gradient(135deg, #283593 0%, #6a1b9a 100%)'
      : '#252525'};
    border-color: ${props => props.$ready ? '#5c6bc0' : '#444'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #1a1a1a;
    border-color: #333;
    box-shadow: none;
  }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default function PinLoginForm({ onLogin, linkedPin = null }) {
  const { t } = useTranslation();
  const [pin, setPin] = useState(linkedPin || '');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const linkedPinSent = useRef(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!pin.trim() || loading) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.AUTH_PIN_LOGIN(), {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinCode: pin.trim() }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || t('pinLogin.invalidPin'));
        setPin('');
        setLoading(false);
        return;
      }

      const instanceId = data.session?.kuutioInstanceId;
      const isAdmin = !instanceId;

      onLogin({
        instanceId,
        isAdmin,
        session: data.session,
      });

    } catch (err) {
      console.error('[PinLoginForm] Login error:', err);
      setError(t('pinLogin.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!linkedPin || linkedPinSent.current) return;
    linkedPinSent.current = true;
    handleSubmit();
  });

  return (
    <Form onSubmit={handleSubmit}>
      <PinInput
        type="text"
        value={pin}
        onChange={(e) => {
          setPin(e.target.value.toUpperCase());
          setError(null);
        }}
        placeholder={t('pinLogin.placeholder')}
        autoFocus
        autoComplete="off"
        $error={!!error}
      />

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <SubmitButton type="submit" disabled={!pin.trim() || loading} $ready={pin.trim().length >= 6}>
        {loading ? <Spinner /> : t('pinLogin.submit')}
      </SubmitButton>
    </Form>
  );
}
